import type { JobArtifact, PackagingJob } from '../types/job'
import { withoutVendorQuote } from './jobError'

/**
 * The packaging pipeline, as the reference's job modal lists it
 * (sap-mockup/app/(console)/jobs/page.tsx `PIPELINE`), plus the virus scan the packager runs.
 *
 * The reference derives its ticks from the status alone, so a Completed job ticks all six whether
 * or not they happened. Ours reads the run's own evidence — the artifacts it left behind and the
 * catalog app it produced — so a job that completed WITHOUT publishing, or without an .intunewin,
 * says so.
 *
 * What the backend gives us to read (tenant-backend/workers/tasks.py):
 *   - `log` (execution.log) and `report` (aeam_report.md) are stored once the packager returns;
 *   - `psadt_package` / `intune_package` / `full_package` are the files it produced;
 *   - `result_app_id` is set when the catalog app is created;
 *   - `aeam_report` is the packager's own test summary text, which carries the virus-scan
 *     verdicts (see `virusScanStep`). GET /jobs leaves it out of its rows, so the job list
 *     cannot supply it; the `report` artifact holds the same text and is passed in instead.
 * Progress covers the run coarsely: 10 = provisioning a VM, 30 = the packager starts, 70 = its
 * artifacts are downloaded, 90 = zipped, 100 = published. Steps 1-5 all happen inside the packager
 * between 30 and 70, which is why a RUNNING job can only mark the earliest unproven step.
 */
export const PIPELINE_STEPS = [
  'Fetch package metadata',
  'Analyze installer & detect silent args',
  'Build PSADT package',
  'Build Intune package (.intunewin)',
  'Automated install → detect → uninstall test (clean VM)',
  'Virus & malware scan',
  'Publish to catalog',
] as const

const TEST_STEP = 4
const SCAN_STEP = 5

export type StepState = 'done' | 'running' | 'failed' | 'warning' | 'skipped' | 'pending'

export interface PipelineStep {
  label: string
  state: StepState
  /** Why the step is in that state, when the state alone would not say. */
  note?: string
}

/** The packager reports a completed job that failed one of its tests through the error message. */
const isPartialSuccess = (job: PackagingJob) =>
  job.status === 'completed' &&
  !!job.error_message &&
  /partial success|uninstall|with warnings/i.test(job.error_message)

const scanVerdict = (report: string, line: RegExp): string | null => {
  const m = report.match(line)
  return m ? m[1].toUpperCase() : null
}

/**
 * The packager checks the installer's reputation before installing and the installed files'
 * after (Webroot BrightCloud; aeam/tester/file_reputation.py), and prints each verdict twice in
 * the text that becomes `aeam_report` — as a test step ("Pre-Install Reputation") and in its
 * "Virus Scan Summary" ("Installer Virus Scan") — so either line is proof. A run whose report
 * carries no verdict at all proved nothing, and the step stays pending like any unproven step.
 */
function virusScanStep(job: PackagingJob, reportText: string | null | undefined): PipelineStep {
  const label = PIPELINE_STEPS[SCAN_STEP]
  const report = job.aeam_report || reportText || ''
  const pre = scanVerdict(report, /(?:Pre-Install Reputation|Installer Virus Scan):\s*(SUCCESS|FAILURE|SKIPPED)/i)
  const post = scanVerdict(report, /(?:Post-Install Reputation|Post-Install Virus Scan):\s*(SUCCESS|FAILURE|SKIPPED)/i)

  const failedOnReputation =
    job.status === 'failed' &&
    !!job.error_message &&
    /reputation|virus|malware|threat/i.test(withoutVendorQuote(job.error_message))

  if (pre === 'FAILURE' || post === 'FAILURE' || failedOnReputation) {
    return { label, state: 'failed', note: 'Threat reported — see the Report tab' }
  }
  if (pre === 'SUCCESS' && post === 'SUCCESS') {
    return { label, state: 'done', note: 'Installer and installed files clean' }
  }
  if (pre === 'SUCCESS') return { label, state: 'done', note: 'Installer clean; post-install scan skipped' }
  if (post === 'SUCCESS') return { label, state: 'done', note: 'Installed files clean; installer scan skipped' }
  if (pre === 'SKIPPED' || post === 'SKIPPED') return { label, state: 'skipped', note: 'Scan disabled' }
  return { label, state: 'pending' }
}

/**
 * @param report the `report` artifact's text (aeam_report.md), read for the scan verdicts when the
 *   job object carries no `aeam_report` of its own.
 */
export function jobPipeline(
  job: PackagingJob,
  artifacts: JobArtifact[],
  report?: string | null
): PipelineStep[] {
  const has = (type: string) => artifacts.some((a) => a.artifact_type === type)
  const anyPackage = has('psadt_package') || has('intune_package') || has('full_package')
  const requested = (method: string) => job.deployment_methods.includes(method)

  // Evidence that the step ACTUALLY happened, never an inference from the status.
  // The scan step's entry is a placeholder: it proves itself through the report text instead.
  const proven = [
    anyPackage || has('report') || has('log') || job.progress_pct >= 70,
    anyPackage || has('report'),
    has('psadt_package') || has('full_package'),
    has('intune_package'),
    has('report') || job.status === 'completed',
    false,
    !!job.result_app_id,
  ]

  // A method that was never asked for is not a missing step.
  const skipped = [false, false, !requested('psadt'), !requested('intune'), false, false, false]

  const steps: PipelineStep[] = PIPELINE_STEPS.map((label, i) => {
    if (i === SCAN_STEP) return virusScanStep(job, report)
    if (skipped[i]) return { label, state: 'skipped', note: 'Not requested' }
    if (proven[i]) {
      if (i === TEST_STEP && isPartialSuccess(job)) return { label, state: 'warning', note: 'Passed with warnings' }
      return { label, state: 'done' }
    }
    return { label, state: 'pending' }
  })

  const firstUnproven = steps.findIndex((s) => s.state === 'pending')

  if (job.status === 'failed') {
    // A scan that reported a threat already names the killer; a second red row would blame a
    // step that never got the chance to run.
    if (steps[SCAN_STEP].state === 'failed') return steps
    if (firstUnproven >= 0) {
      // The scan proves itself only through the report text, so a run without a verdict must not
      // swallow a failure that belongs to the step after it.
      const anchor =
        firstUnproven === SCAN_STEP
          ? steps.findIndex((s, i) => i > SCAN_STEP && s.state === 'pending')
          : firstUnproven
      if (anchor >= 0) steps[anchor] = { label: steps[anchor].label, state: 'failed' }
    }
    return steps
  }

  if (job.status === 'cancelled') {
    return steps.map((s) => (s.state === 'pending' ? { ...s, state: 'skipped', note: 'Not run' } : s))
  }

  if (job.status === 'processing' && firstUnproven >= 0) {
    // Below 30 the packager has not started — the job is waiting on a clean VM, and saying "Fetch
    // package metadata" is running would be a guess.
    return job.progress_pct >= 30
      ? steps.map((s, i) => (i === firstUnproven ? { ...s, state: 'running' } : s))
      : steps.map((s, i) => (i === firstUnproven ? { ...s, note: 'Waiting for a packaging VM' } : s))
  }

  return steps
}
